import {
  faNewspaper,
  faCheck,
  faFilter,
  faInfoCircle,
  faPen,
  faPlus,
  faSearch,
  faTimes,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "bootstrap/dist/css/bootstrap.min.css";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAuthenticatedAPI } from "../hooks/useAuthenticatedAPI";
import ProcessLoading from "../components/processLoading";
import "../styles/gestionArticle.css";

const AdminActualitePage = () => {
  const { client, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { apiGet, apiPost, apiPut, apiDelete, API_BASE_URL } =
    useAuthenticatedAPI();

  // États principaux
  const [publications, setPublications] = useState([]);
  const [selectedPublication, setSelectedPublication] = useState(null);

  // États pour la recherche
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // États pour le modal
  const [showModal, setShowModal] = useState(false);
  const [currentPublication, setCurrentPublication] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // États de chargement et erreurs
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Vérification des autorisations au chargement
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/");
      return;
    }

    if (client?.role === "client") {
      setError(
        "Vous n'avez pas les autorisations nécessaires pour accéder à cette page"
      );
      setTimeout(() => navigate("/dashboard"), 2000);
      return;
    }

    loadPublications();
  }, [client, isAuthenticated, navigate]);

  // Chargement des publications
  const loadPublications = async () => {
    setPageLoading(true);
    try {
      await fetchPublications();
    } catch (err) {
      setError("Erreur lors du chargement des actualités");
      console.error("Erreur:", err);
    } finally {
      setPageLoading(false);
    }
  };

  // Récupérer toutes les publications
  const fetchPublications = async () => {
    try {
      const data = await apiGet("/publication/getpub");
      const publicationsArray = Array.isArray(data.publications)
        ? data.publications
        : [];
      setPublications(publicationsArray);
      if (publicationsArray.length > 0 && !selectedPublication) {
        setSelectedPublication(publicationsArray[0]);
      }
    } catch (err) {
      console.error("Erreur fetch publications:", err);
      setError(err.message || "Erreur lors de la récupération des actualités");
    }
  };

  // Recherche d'actualités par label
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const data = await apiGet(
        `/publication/pub/search?label=${encodeURIComponent(searchTerm)}`
      );
      const results = Array.isArray(data.publications) ? data.publications : [];
      setSearchResults(results);
      setIsSearching(true);
      setCurrentPage(1);
    } catch (err) {
      console.error("Erreur recherche:", err);
      // Si pas de résultat, afficher message approprié
      if (err.message.includes("404")) {
        setSearchResults([]);
        setIsSearching(true);
      } else {
        setError("Erreur lors de la recherche");
      }
    } finally {
      setLoading(false);
    }
  };

  // Réinitialiser la recherche
  const clearSearch = () => {
    setSearchTerm("");
    setIsSearching(false);
    setSearchResults([]);
    setCurrentPage(1);
  };

  // Obtenir les publications à afficher
  const getDisplayedPublications = () => {
    if (isSearching) return searchResults;
    return publications;
  };

  // Pagination
  const displayedPublications = getDisplayedPublications();
  const totalPages = Math.ceil(displayedPublications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPublications = displayedPublications.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Fonctions du modal
  const openCreateModal = () => {
    setIsEditing(false);
    setCurrentPublication({
      label: "",
      description: "",
      image: null,
    });
    setPreviewImage(null);
    setShowModal(true);
  };

  const openEditModal = (publication) => {
    setIsEditing(true);
    setCurrentPublication({
      ...publication,
      image: null, // Pour le nouvel upload
    });
    // Afficher l'image existante
    if (publication.image) {
      setPreviewImage(
        `https://boutiqu.transfoafricainc.com/${publication.image}`
      );
    } else {
      setPreviewImage(null);
    }
    setShowModal(true);
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentPublication((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCurrentPublication((prev) => ({
      ...prev,
      image: file,
    }));

    // Créer l'aperçu
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Supprimer l'image sélectionnée
  const removeImage = () => {
    setCurrentPublication((prev) => ({
      ...prev,
      image: null,
    }));
    setPreviewImage(null);
  };

  // Sauvegarde
  const handleSave = async () => {
    setLoading(true);
    try {
      if (!currentPublication.label || !currentPublication.description) {
        setError("Veuillez remplir tous les champs obligatoires");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("label", currentPublication.label.trim());
      formData.append("description", currentPublication.description.trim());

      // Ajouter l'image si elle existe
      if (currentPublication.image instanceof File) {
        formData.append("image", currentPublication.image);
      } else if (!isEditing) {
        // Pour création, l'image est obligatoire
        setError("Veuillez ajouter une image");
        setLoading(false);
        return;
      }

      let response;
      if (isEditing) {
        response = await apiPut(
          `/publication/updatepub/${currentPublication.id_pub}`,
          formData
        );
      } else {
        response = await apiPost("/publication/createpub", formData);
      }

      setSuccess(
        isEditing
          ? "Actualité modifiée avec succès"
          : "Actualité créée avec succès"
      );
      setShowModal(false);
      setPreviewImage(null);
      await fetchPublications();

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Erreur lors de la sauvegarde:", err);
      setError(err.message || "Erreur lors de la sauvegarde");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Suppression
  const handleDelete = async (id_pub) => {
    if (!window.confirm("Voulez-vous vraiment supprimer cette actualité ?"))
      return;

    setLoading(true);
    try {
      await apiDelete(`/publication/deletepub/${id_pub}`);
      setSuccess("Actualité supprimée avec succès");

      if (selectedPublication?.id_pub === id_pub) {
        setSelectedPublication(
          publications.length > 1
            ? publications.find((p) => p.id_pub !== id_pub)
            : null
        );
      }

      await fetchPublications();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Erreur lors de la suppression");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return "Date inconnue";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (pageLoading) {
    return <ProcessLoading message="Chargement des actualités..." />;
  }

  return (
    <div className="container-fluid p-4 bg-light">
      {/* Messages d'erreur et succès */}
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}

      {success && (
        <div
          className="alert alert-success alert-dismissible fade show"
          role="alert"
        >
          {success}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccess("")}
          ></button>
        </div>
      )}

      {/* En-tête */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faNewspaper} className="me-2 text-primary" />
            Gestion des Actualités (Administration)
          </h4>
          <small className="text-muted">
            {isSearching
              ? `${displayedPublications.length} résultat(s) de recherche`
              : `${displayedPublications.length} actualité(s)`}{" "}
            • Page {currentPage}/{totalPages}
          </small>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Nouvelle Actualité
        </button>
      </div>

      {/* Barre de recherche */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            <FontAwesomeIcon icon={faSearch} className="me-2" />
            Rechercher une actualité
          </h5>
          <div className="row g-3">
            <div className="col-md-8">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher par titre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
                {isSearching && (
                  <button className="btn btn-secondary" onClick={clearSearch}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Message si aucun résultat */}
      {isSearching && displayedPublications.length === 0 && (
        <div className="alert alert-info text-center">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          Aucune actualité trouvée pour "{searchTerm}"
        </div>
      )}

      {/* Contenu principal */}
      <div className="row">
        {/* Liste des actualités */}
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Image</th>
                      <th>Titre</th>
                      <th>Description</th>
                      <th>Date de création</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPublications.map((publication) => (
                      <tr
                        key={publication.id_pub}
                        onClick={() => setSelectedPublication(publication)}
                        className={
                          selectedPublication?.id_pub === publication.id_pub
                            ? "table-primary"
                            : ""
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <td className="fw-bold">{publication.id_pub}</td>
                        <td>
                          {publication.image ? (
                            <img
                              src={`https://boutiqu.transfoafricainc.com/${publication.image}`}
                              alt={publication.label}
                              style={{
                                width: "60px",
                                height: "60px",
                                objectFit: "cover",
                                objectPosition: "center",
                              }}
                              className="rounded border"
                            />
                          ) : (
                            <div
                              className="bg-light rounded border d-flex align-items-center justify-content-center"
                              style={{ width: "60px", height: "60px" }}
                            >
                              <FontAwesomeIcon
                                icon={faImage}
                                className="text-muted"
                              />
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="fw-bold">{publication.label}</div>
                        </td>
                        <td>
                          <small className="text-muted">
                            {publication.description?.substring(0, 50)}...
                          </small>
                        </td>
                        <td>
                          <small>{formatDate(publication.createdAt)}</small>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-danger me-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(publication.id_pub);
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(publication);
                            }}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="card-footer">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Affichage de {startIndex + 1} à{" "}
                    {Math.min(
                      startIndex + itemsPerPage,
                      displayedPublications.length
                    )}
                    sur {displayedPublications.length} actualités
                  </small>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                      </li>

                      {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 2 && page <= currentPage + 2)
                        ) {
                          return (
                            <li
                              key={page}
                              className={`page-item ${
                                currentPage === page ? "active" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </button>
                            </li>
                          );
                        } else if (
                          page === currentPage - 3 ||
                          page === currentPage + 3
                        ) {
                          return (
                            <li key={page} className="page-item disabled">
                              <span className="page-link">...</span>
                            </li>
                          );
                        }
                        return null;
                      })}

                      <li
                        className={`page-item ${
                          currentPage === totalPages ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Détails de l'actualité */}
        <div className="col-lg-4">
          {selectedPublication ? (
            <div className="card shadow-sm h-100">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">Détails de l'actualité</h5>
              </div>
              <div className="card-body">
                <div className="text-center mb-3">
                  {selectedPublication.image ? (
                    <img
                      src={`https://boutiqu.transfoafricainc.com/${selectedPublication.image}`}
                      alt={selectedPublication.label}
                      className="img-fluid rounded border"
                      style={{
                        maxHeight: "250px",
                        width: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                      }}
                    />
                  ) : (
                    <div
                      className="bg-light rounded border d-flex align-items-center justify-content-center"
                      style={{ width: "100%", height: "250px" }}
                    >
                      <FontAwesomeIcon
                        icon={faImage}
                        size="3x"
                        className="text-secondary"
                      />
                    </div>
                  )}
                </div>

                <h5 className="text-primary mb-3">
                  {selectedPublication.label}
                </h5>

                <div className="mb-3">
                  <h6 className="text-muted">INFORMATIONS</h6>
                  <hr className="mt-1" />
                  <div className="row">
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">ID</small>
                      <span>{selectedPublication.id_pub}</span>
                    </div>
                    <div className="col-12 mb-2">
                      <small className="text-muted d-block">
                        Date de création
                      </small>
                      <span>{formatDate(selectedPublication.createdAt)}</span>
                    </div>
                    <div className="col-12 mb-2">
                      <small className="text-muted d-block">
                        Dernière modification
                      </small>
                      <span>{formatDate(selectedPublication.updatedAt)}</span>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="text-muted">DESCRIPTION</h6>
                  <hr className="mt-1" />
                  <p>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="me-2 text-muted"
                    />
                    {selectedPublication.description}
                  </p>
                </div>

                <div className="d-grid gap-2 mt-4">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => openEditModal(selectedPublication)}
                  >
                    <FontAwesomeIcon icon={faPen} className="me-2" />
                    Modifier l'actualité
                  </button>
                  <button
                    className="btn btn-outline-danger"
                    onClick={() => handleDelete(selectedPublication.id_pub)}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Supprimer l'actualité
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="card shadow-sm h-100">
              <div className="card-body d-flex align-items-center justify-content-center">
                <p className="text-muted mb-0">
                  Sélectionnez une actualité pour afficher les détails
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal pour ajouter/modifier */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "700px" }}>
            <div className="modal-header">
              <h5>
                {isEditing ? "Modifier l'actualité" : "Ajouter une actualité"}
              </h5>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div
              className="modal-body"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              <div className="mb-3">
                <label className="form-label">Titre de l'actualité *</label>
                <input
                  type="text"
                  className="form-control"
                  name="label"
                  value={currentPublication.label}
                  onChange={handleInputChange}
                  placeholder="Ex: Nouvelle promotion sur les produits..."
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Description *</label>
                <textarea
                  className="form-control"
                  name="description"
                  rows="5"
                  value={currentPublication.description}
                  onChange={handleInputChange}
                  placeholder="Décrivez votre actualité en détail..."
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Image {!isEditing && "*"}
                  {isEditing && (
                    <small className="text-muted">
                      {" "}
                      (laissez vide pour conserver l'image actuelle)
                    </small>
                  )}
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>

              {previewImage && (
                <div className="mb-3 text-center">
                  <label className="form-label d-block">
                    Aperçu de l'image
                  </label>
                  <div className="position-relative d-inline-block">
                    <img
                      src={previewImage}
                      alt="Aperçu"
                      className="img-fluid rounded border"
                      style={{
                        maxHeight: "250px",
                        maxWidth: "100%",
                        objectFit: "contain",
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute"
                      style={{ top: 8, right: 8 }}
                      onClick={removeImage}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={loading}
              >
                Annuler
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={
                  loading ||
                  !currentPublication.label ||
                  !currentPublication.description
                }
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {isEditing ? "Modification..." : "Création..."}
                  </>
                ) : (
                  <>{isEditing ? "Enregistrer" : "Ajouter"}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && <ProcessLoading message="Traitement en cours..." />}
    </div>
  );
};

export default AdminActualitePage;
